// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { AsyncPipe, NgClass, NgFor, NgIf } from '@angular/common';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable, combineLatest, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  SrsConnectivityModel,
  SystemDetailsModel,
} from '../../../../api/remote-api/.portal.generated/models';
import { LocalizedDatePipe } from '../../../shared/pipes/localizedDate.pipe';
import { SanitizeResourceUrlPipe } from '../../../shared/pipes/sanitizeResourceUrl.pipe';
import { DeepReadonly } from '../../../shared/utils/api-call-state.util';
import { ResourceService } from '../../services/resource.service';
import { SystemOverviewService } from '../../services/systemOverview.service';

const srsFields = {
  systemInformationData: [
    {
      label: 'label.EQ_INSTALL_DT',
      value: 'eQ_INSTALL_DT' as keyof SrsConnectivityModel,
    },
    {
      label: 'label.EQ_EOD_DT',
      value: 'eQ_EOD_DT' as keyof SrsConnectivityModel,
    },
    {
      label: 'label.EQ_EOS_DT',
      value: 'eQ_EOS_DT' as keyof SrsConnectivityModel,
    },
  ],
  srsConnectivityData: [
    // {
    //   label: 'label.SRS_FTP_LAST_RECEIVED_DT_TM',
    //   value: 'srS_FTP_LAST_RECEIVED_DT_TM' as keyof SrsConnectivityModel,
    // },
    // {
    //   label: 'label.SRS_LAST_PROACTIVE_DT',
    //   value: 'srS_LAST_PROACTIVE_DT' as keyof SrsConnectivityModel,
    // },
    // {
    //   label: 'label.SRS_LAST_REACTIVE_DT',
    //   value: 'srS_LAST_REACTIVE_DT' as keyof SrsConnectivityModel,
    // },
    {
      label: 'label.SRS_CONNECTION_LEVEL',
      value: 'srS_CONNECTION_LEVEL' as keyof SrsConnectivityModel,
      tooltip: 'TODO',
    },
    {
      label: 'label.SRS_STATUS_TEXT',
      value: 'srS_STATUS_TEXT' as keyof SrsConnectivityModel,
      tooltip: false,
    },
  ],
};

const connectivityTooltipMap = {
  'connected not ruh ready':
    'label.SRS_CONNECTION_LEVEL.tooltip.connected_not_ruh_ready',
  'RUH ready': 'label.SRS_CONNECTION_LEVEL.tooltip.ruh_ready',
};

const green_icon = 'success';
const yellow_icon = 'warning';
const red_icon = 'error';
const unknown_icon = 'no-findings';

@Component({
  selector: 'app-system-details',
  templateUrl: './system-details.component.html',
  styleUrls: ['./system-details.component.scss'],
  standalone: true,

  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    NgClass,
    NgFor,
    NgIf,
    LocalizedDatePipe,
    AsyncPipe,
    TranslateModule,
    SanitizeResourceUrlPipe,
  ],
})
export class SystemDetailsComponent implements OnChanges {
  @Input() systemDetails?: DeepReadonly<SystemDetailsModel>;

  ACTIVE_EQUIPMENT_STATE = 'active';
  isactiveSystem?: boolean;
  public showHideToggle?: boolean;
  systemInformationData?: { label: string; value: string }[];
  srsConnectivityData?: {
    label: string;
    value: string | undefined;
    tooltip?: string;
  }[];

  srsConnectivity$ = this.systemOverviewService.srsConnectivity.data$;

  connectivity_status_icon$ = combineLatest([
    this.systemOverviewService.srsConnectivity.data$,
  ])
    .pipe(
      map(([srsConnectivity]) => {
        if (srsConnectivity) {
          return SystemDetailsComponent.computeSRSConnectivityIcon(
            srsConnectivity
          );
        } else {
          return '';
        }
      })
    )
    .pipe(map((icon) => icon || unknown_icon));

  srsConnectivityLoading$ = this.systemOverviewService.srsConnectivity.loading$;
  productPicture = this.resourceService.productPicure.data$.pipe(
    map((imageBlob: any) => {
      try {
        return imageBlob
          ? this.domSanitizer.bypassSecurityTrustUrl(
              URL.createObjectURL(imageBlob)
            )
          : undefined;
      } catch {
        return undefined;
      }
    }),
    catchError(() => {
      return of(undefined);
    })
  );

  hasProductPicture$ = this.productPicture.pipe(map((p) => !!p));

  constructor(
    public readonly systemOverviewService: SystemOverviewService,
    private readonly translateService: TranslateService,
    public readonly resourceService: ResourceService,
    private readonly domSanitizer: DomSanitizer
  ) {}

  ngOnChanges(simpleChanges: SimpleChanges): void {
    if (simpleChanges['systemDetails']) {
      this.isactiveSystem = this.systemDetails?.equipmentActive === this.ACTIVE_EQUIPMENT_STATE;
      this.resourceService.productPicure.reset();
      this.systemOverviewService.srsConnectivity.reset();
      // options to clear state data right away!
      if (this.systemDetails) {
        this.systemOverviewService.srsConnectivity.request({
          systemMaterialNumber: this.systemDetails.systemMaterialNumber,
          systemSerialNumber: this.systemDetails.systemSerialNumber,
        });
        this.resourceService.productPicure.request({
          systemMaterialNumber: this.systemDetails.systemMaterialNumber,
        });
      }
    }
  }

  get isSystemDetailsLoaded(): boolean {
    return !!this.systemDetails;
  }

  get srsFields(): typeof srsFields {
    return srsFields;
  }

  get connectivityTooltipMap(): typeof connectivityTooltipMap {
    return connectivityTooltipMap;
  }

  //TODO - double check typeassertion
  srsField(key: keyof SrsConnectivityModel): Observable<string | Date> {
    return this.srsConnectivity$.pipe(
      map((srs) => {
        const value = srs?.[key] as string | Date;
        return value || 'unknown';
      }),
      map((srs) => {
        return srs === 'null' ? 'OK' : srs;
      })
    );
  }

  static computeSRSConnectivityIcon(
    srsConnectivity?: SrsConnectivityModel
  ): string {
    if (srsConnectivity?.srS_CONNECTIVITY_STATUS?.match(/expired/i)) {
      return red_icon;
    }

    // see request for this here: https://dev.azure.com/shcgravity/Service%20Software%20Online%20(SeSo)/_workitems/edit/151155#7692407
    if (
      srsConnectivity?.srS_CONNECTIVITY_STATUS?.match(/OK/i) &&
      /NULL/i.exec(srsConnectivity?.srS_STATUS_TEXT || '')
    ) {
      return green_icon;
    }

    if (
      srsConnectivity?.srS_CONNECTIVITY_STATUS?.match(/OK/i) &&
      srsConnectivity?.srS_STATUS_TEXT?.match(/OK/i)
    ) {
      return green_icon;
    }

    return yellow_icon;
  }
}
